import { EHomeStatus } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { Address } from '@src/types/AddressSchema'
import {
  ApplicantDto,
  ApplicantExpenses,
  ApplicantJob,
  CreditApplication,
  DraftCreditApplicationDto,
} from '@src/types/CreditApplicationSchema'
import { omit, toInteger } from 'lodash-es'

export function transformApplicantExpensesToApi(expenses: ApplicantExpenses): ApplicantExpenses {
  if (expenses.homeFeeTypeId === EHomeStatus.Owner) {
    expenses.rentMonthly = null
  }
  if (expenses.homeFeeTypeId === EHomeStatus.OwnWithMortgage) {
    expenses.mortgageMonthly = expenses.housingMonthly
    expenses.rentMonthly = null
  } else {
    expenses.rentMonthly = expenses.housingMonthly
  }

  return expenses
}

function transformApplicantJobToApi(job: ApplicantJob): ApplicantJob {
  const ret = omit(job, 'years') as ApplicantJob
  ret.months = (toInteger(job.years) ?? 0) * 12 + (toInteger(job.months) ?? 0)
  return ret
}

function transformApplicantAddressToApi(address: Address): Address {
  const ret = omit(address, 'years') as Address
  ret.months = toInteger((address.years ?? 0) * 12) + toInteger(address.months ?? 0)
  return ret
}

function transformExpenseFromApi(expenses: ApplicantExpenses): ApplicantExpenses {
  if (expenses.rentMonthly || expenses.mortgageMonthly)
    expenses.housingMonthly = expenses.rentMonthly! + expenses.mortgageMonthly!
  return expenses
}

export function transformApplicantJobFromApi(job: ApplicantJob): ApplicantJob {
  const maxYears = 15
  const ret = { ...job } as ApplicantJob
  if (job.months !== null) {
    ret.months = (job.months ?? 0) % 12
    ret.years = ((job.months ?? 0) - ret.months) / 12
    if (ret.years >= maxYears) ret.years = maxYears
    if (ret.months === 0) ret.months = null
  } else {
    ret.months = null
    ret.years = 0
  }
  // Ensure years is never null for the new behavior
  return { ...ret, years: ret.years ?? 0 } as ApplicantJob
}

export function transformAddressFromApi(currentAddress: Address): Address {
  const maxYears = 15
  const ret = { ...currentAddress } as Address
  if (currentAddress.months !== null) {
    ret.months = (currentAddress.months ?? 0) % 12
    ret.years = ((currentAddress.months ?? 0) - ret.months) / 12
    if (ret.years >= maxYears) ret.years = maxYears
    if (ret.months === 0) ret.months = null
  } else {
    ret.months = null
    ret.years = 0
  }
  // Ensure years is never null for the new behavior
  return { ...ret, years: ret.years ?? 0 } as Address
}

export function transformApplicantToApi(applicant: ApplicantDto) {
  return {
    ...applicant,
    expenses: transformApplicantExpensesToApi(applicant.expenses!),
    currentJobs: applicant.currentJobs.map(transformApplicantJobToApi),
    previousJobs: (applicant.previousJobs ?? []).map(transformApplicantJobToApi),
    currentAddress: transformApplicantAddressToApi(applicant.currentAddress as Address),
  } as ApplicantDto
}

function transformApplicantFromApi(applicant: ApplicantDto) {
  return {
    ...applicant,
    expenses: transformExpenseFromApi(applicant.expenses!),
    currentJobs: applicant.currentJobs.map(transformApplicantJobFromApi),
    previousJobs: (applicant.previousJobs ?? []).map(transformApplicantJobFromApi),
    currentAddress: transformAddressFromApi(applicant.currentAddress as Address),
    creditAlerts: applicant.creditAlerts || {
      hasNameMismatch: false,
      hasDoBMismatch: false,
      hasPreviousApplication: false,
      hasCreditLocked: false,
      hasInvalidCity: false,
      hasGeneralCreditReportError: false,
      isBanned: false,
    },
    isApplicantNewToCredit: applicant.isApplicantNewToCredit ?? false,
  } as ApplicantDto
}

export function transformCreditApplicationToApi(creditApp: Partial<DraftCreditApplicationDto>) {
  return {
    ...creditApp,
    applicant: creditApp.applicant ? transformApplicantToApi(creditApp.applicant as ApplicantDto) : null,
    coApplicant: creditApp?.coApplicant ? transformApplicantToApi(creditApp.coApplicant as ApplicantDto) : null,
  }
}

export function transformCreditApplicationFromApi(creditApp: CreditApplication) {
  return {
    ...creditApp,
    worksheet: creditApp.worksheet
      ? { ...creditApp.worksheet, firstPaymentOn: formatDate(creditApp.worksheet.firstPaymentOn) }
      : null,
    applicant: transformApplicantFromApi(creditApp.applicant),
    coApplicant: creditApp?.coApplicant ? transformApplicantFromApi(creditApp.coApplicant) : null,
  }
}
